import { create } from 'zustand';
import { authService, AuthResponse } from '../services/auth.service';

// Задержки между повторными попытками входа (мс). Итого до 3 попыток (~2.4с).
const AUTH_RETRY_DELAYS_MS = [800, 1600];

// Повторяем только транзиентные сбои: нет ответа (сеть/таймаут), троттлинг (429) или 5xx.
// На 400/401/403 (реальный отказ Telegram) повтор бессмысленен — тот же initData снова упадёт.
function isRetryableAuthError(err: any): boolean {
  const status = err?.response?.status;
  if (status === undefined) return true;
  return status === 429 || (status >= 500 && status < 600);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface AuthState {
  isAuthenticated: boolean;
  user: AuthResponse['user'] | null;
  loading: boolean;
  error: string | null;
  authenticate: (initData: string) => Promise<void>;
  authenticateByUsername: (username: string, browserE2eToken: string) => Promise<void>;
  logout: () => void;
  setUser: (user: AuthResponse['user']) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: !!localStorage.getItem('accessToken'),
  user: null,
  loading: false,
  error: null,
  authenticate: async (initData: string) => {
    set({ loading: true, error: null });
    const maxAttempts = AUTH_RETRY_DELAYS_MS.length + 1;
    let lastError: any;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await authService.authenticate(initData);
        set({
          isAuthenticated: true,
          user: response.user,
          loading: false,
        });
        return;
      } catch (error: any) {
        lastError = error;
        if (attempt < maxAttempts && isRetryableAuthError(error)) {
          await sleep(AUTH_RETRY_DELAYS_MS[attempt - 1]);
          continue;
        }
        break;
      }
    }
    set({
      isAuthenticated: false,
      user: null,
      loading: false,
      error: lastError?.message || 'Authentication failed',
    });
    throw lastError;
  },
  authenticateByUsername: async (username: string, browserE2eToken: string) => {
    set({ loading: true, error: null });
    try {
      const response = await authService.authenticateByUsername(username, browserE2eToken);
      set({
        isAuthenticated: true,
        user: response.user,
        loading: false,
      });
    } catch (error: any) {
      set({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: error.message || 'Authentication failed',
      });
      throw error;
    }
  },
  logout: () => {
    localStorage.removeItem('accessToken');
    set({
      isAuthenticated: false,
      user: null,
      error: null,
    });
  },
  setUser: (user: AuthResponse['user']) => {
    set({ user });
  },
}));

